import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { isDividerTableRowCells, type MoveDocumentTableImageInput } from '@vivport/backend/types';
import type { Element as HastElement, Root as HastRoot } from 'hast';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

export interface DocumentImageDragOptions {
  documentId: number;
  disabled?: boolean;
  onMove: (input: MoveDocumentTableImageInput) => void;
}

export interface TableImageCoordinates {
  tableIndex: number;
  rowIndex: number;
  columnIndex: number;
}

const DATA_TABLE = 'data-vp-table';
const DATA_ROW = 'data-vp-row';
const DATA_COLUMN = 'data-vp-column';
const DATA_DIVIDER = 'data-vp-divider';

const DragContext = createContext<DocumentImageDragOptions | null>(null);

export function useDocumentImageDrag(): DocumentImageDragOptions | null {
  return useContext(DragContext);
}

function textOf(node: HastElement): string {
  let out = '';
  const walk = (child: { type: string; value?: string; children?: unknown[]; tagName?: string }): void => {
    if (child.type === 'text') {
      out += child.value ?? '';
      return;
    }
    if (child.type === 'element') {
      if (child.tagName === 'img') {
        out += '![image](ref)';
        return;
      }
      for (const grandchild of (child.children ?? []) as typeof child[]) walk(grandchild);
    }
  };
  for (const child of node.children as unknown as Parameters<typeof walk>[0][]) walk(child);
  return out;
}

function imageDescendants(node: HastElement): HastElement[] {
  const found: HastElement[] = [];
  const walk = (current: HastElement): void => {
    for (const child of current.children) {
      if (child.type !== 'element') continue;
      if (child.tagName === 'img') found.push(child);
      else walk(child);
    }
  };
  walk(node);
  return found;
}

/**
 * Stamps each body cell with its (table, row, column) BEFORE react-markdown renders components,
 * because a component only ever sees its own node and cannot count its siblings. Row indices count
 * from the first row after the separator and include divider bands — the same contract
 * indexMarkdownTableImages obeys on the backend, which is what lets the UI send coordinates the
 * move logic can act on without re-deriving them.
 */
export function rehypeStampTableCoordinates() {
  return (tree: HastRoot): void => {
    let tableIndex = 0;
    const visit = (node: HastElement | HastRoot): void => {
      for (const child of node.children) {
        if (child.type !== 'element') continue;
        if (child.tagName === 'table') {
          const currentTable = tableIndex++;
          const body = child.children.find(
            (candidate): candidate is HastElement =>
              candidate.type === 'element' && candidate.tagName === 'tbody'
          );
          if (body) {
            let rowIndex = 0;
            for (const row of body.children) {
              if (row.type !== 'element' || row.tagName !== 'tr') continue;
              const cells = row.children.filter(
                (candidate): candidate is HastElement =>
                  candidate.type === 'element' && candidate.tagName === 'td'
              );
              const divider = isDividerTableRowCells(cells.map(textOf));
              cells.forEach((cell, columnIndex) => {
                const stamp = {
                  [DATA_TABLE]: String(currentTable),
                  [DATA_ROW]: String(rowIndex),
                  [DATA_COLUMN]: String(columnIndex),
                };
                cell.properties = {
                  ...cell.properties,
                  ...stamp,
                  ...(divider ? { [DATA_DIVIDER]: 'true' } : {}),
                };
                // The image carries the same stamp: react-markdown hands each component only its
                // own node, so an <img> cannot otherwise learn which cell it is sitting in.
                for (const image of imageDescendants(cell)) {
                  image.properties = { ...image.properties, ...stamp };
                }
              });
              rowIndex++;
            }
          }
        }
        visit(child);
      }
    };
    visit(tree);
  };
}

export function coordinatesFromCellProps(props: Record<string, unknown>): TableImageCoordinates | null {
  const table = props[DATA_TABLE];
  const row = props[DATA_ROW];
  const column = props[DATA_COLUMN];
  if (typeof table !== 'string' || typeof row !== 'string' || typeof column !== 'string') return null;
  return { tableIndex: Number(table), rowIndex: Number(row), columnIndex: Number(column) };
}

export function isDividerCellProps(props: Record<string, unknown>): boolean {
  return props[DATA_DIVIDER] === 'true';
}

const cellId = (at: TableImageCoordinates): string =>
  `cell:${at.tableIndex}:${at.rowIndex}:${at.columnIndex}`;

interface DragPayload {
  code: string;
  from: TableImageCoordinates;
}

export function DraggableTableImage({
  code,
  at,
  children,
}: {
  code: string;
  at: TableImageCoordinates;
  children: ReactNode;
}) {
  const drag = useDocumentImageDrag();
  const payload: DragPayload = { code, from: at };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `image:${code}:${cellId(at)}`,
    data: payload,
    disabled: drag?.disabled ?? false,
  });
  return (
    <span
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={isDragging ? 'inline-block cursor-grabbing opacity-40' : 'inline-block cursor-grab'}
      title="Drag to move this photo to another row"
    >
      {children}
    </span>
  );
}

export function DroppableTableCell({
  at,
  isDivider,
  className,
  children,
  ...props
}: {
  at: TableImageCoordinates;
  isDivider: boolean;
  className?: string;
  children?: ReactNode;
} & React.TdHTMLAttributes<HTMLTableCellElement>) {
  const { setNodeRef, isOver } = useDroppable({ id: cellId(at), data: at, disabled: isDivider });
  return (
    <td
      ref={setNodeRef}
      className={`${className ?? ''}${isOver && !isDivider ? ' bg-primary/10 outline outline-2 outline-primary/40' : ''}`}
      {...props}
    >
      {children}
    </td>
  );
}

export function DocumentImageDragProvider({
  options,
  children,
}: {
  options: DocumentImageDragOptions;
  children: ReactNode;
}) {
  const [dragging, setDragging] = useState<DragPayload | null>(null);
  // A small activation distance keeps a plain click on an image (which opens it) from being
  // swallowed by the drag sensor.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = (event: DragEndEvent): void => {
    setDragging(null);
    const payload = event.active.data.current as DragPayload | undefined;
    const target = event.over?.data.current as TableImageCoordinates | undefined;
    if (!payload || !target) return;
    if (target.tableIndex !== payload.from.tableIndex) return;
    if (target.rowIndex === payload.from.rowIndex && target.columnIndex === payload.from.columnIndex) {
      return;
    }
    options.onMove({
      documentId: options.documentId,
      imageCode: payload.code,
      tableIndex: payload.from.tableIndex,
      fromRowIndex: payload.from.rowIndex,
      fromColumnIndex: payload.from.columnIndex,
      toRowIndex: target.rowIndex,
      toColumnIndex: target.columnIndex,
    });
  };

  const value = useMemo(() => options, [options]);

  return (
    <DragContext.Provider value={value}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={(event: DragStartEvent) =>
          setDragging((event.active.data.current as DragPayload | undefined) ?? null)
        }
        onDragCancel={() => setDragging(null)}
        onDragEnd={onDragEnd}
      >
        {children}
        <DragOverlay>
          {dragging ? (
            <div className="rounded border border-primary bg-background px-2 py-1 text-xs shadow">
              Moving photo
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </DragContext.Provider>
  );
}
